import { useAuthMe } from '@h2hspark/auth-ui';
import { Skeleton } from '@h2hspark/ui';
import { ScanLine } from 'lucide-react';
import { toString as renderQrSvg } from 'qrcode';
import * as React from 'react';

import { usePublicEvent } from '../event-discovery/api';
import { useMyRegistrations } from '../event-registrations/api';

import { useMyTicket } from './api';
import {
  QR_ALTERNATIVE_TEXT,
  SCAN_AT_CHECK_IN,
  TICKET_BAND_LABEL,
  TICKET_QR_OPTIONS,
  TICKET_UNAVAILABLE_DESCRIPTION,
  TICKET_UNAVAILABLE_TITLE,
  formatTicketDateTime,
  qrPayload,
  qrSvgDataUri,
  registrantName,
  ticketLine,
  venueLine,
} from './constants';

/**
 * **The ticket itself** (`MEM-022` AC-1, AC-2) — the thing a member holds up at a door, drawn as
 * `Web App.dc.html`'s ticket overlay draws it (lines 1137–1148, inventory §2.16).
 *
 * A `--purple-800` band reading *H2HSpark ticket* at `700/var(--text-2xs)`, `.14em`, in
 * `--fuchsia-200`; then the event name at `800/var(--text-xl)`, its date, and the venue and city,
 * over a **2px dashed** divider; then a 168px code on white, the member's name at
 * `700/var(--text-sm)`, *"Admits 1 · order #…"*, and a `scan-line` glyph with *Show this at
 * check-in*.
 *
 * ## One card, three places
 *
 * The route (`/app/events/:id/ticket`, which mailed links point at), the dialog the event page and
 * Your Events open, and nothing else. Each renders this component from {@link useTicketContent},
 * so the ticket a member is shown is assembled once however they reached it.
 *
 * Where each field comes from is `MEM-022` AC-1's own list and is unchanged by the move: the
 * event, the order number and the venue from `/api/event-registrations/mine`; the city from the
 * public event payload, the only place it lives; the name from the session; the code from the
 * ticket token (`MEM-022` AC-2 — and only that token, never the connect one).
 *
 * **Still no identifier, no self check-in and no check-in status** — `EVT-031` AC-6, and
 * `constants.ts`'s `SELF_CHECK_IN_IS_NOT_BUILT` says why.
 */
export interface TicketContent {
  readonly state: 'loading' | 'no-ticket' | 'ready';
  readonly eventName: string;
  readonly when: string | null;
  readonly where: string | null;
  readonly name: string | null;
  readonly confirmationCode: string;
  readonly qrSource: string | null;
  readonly codeUnavailable: boolean;
}

export function useTicketContent(eventId: string): TicketContent {
  const { user } = useAuthMe();
  const registrations = useMyRegistrations({ enabled: true });

  const entry =
    registrations.data?.registrations.find(
      (row) => row.event.eventId === eventId && row.registration.status === 'confirmed',
    ) ?? null;

  // Gated on an actual registration: the API answers 404 for a member without one, and asking
  // anyway would trade the empty state for a failed request.
  const ticket = useMyTicket(eventId, { enabled: entry !== null });
  // The city, and only the city. See the header.
  const publicEvent = usePublicEvent(eventId);

  const payload = ticket.data === undefined ? null : qrPayload(ticket.data);
  const qrSource = useQrCodeSource(payload);

  if (registrations.isPending) {
    return {
      state: 'loading',
      eventName: '',
      when: null,
      where: null,
      name: null,
      confirmationCode: '',
      qrSource: null,
      codeUnavailable: false,
    };
  }
  if (entry === null) {
    return {
      state: 'no-ticket',
      eventName: '',
      when: null,
      where: null,
      name: null,
      confirmationCode: '',
      qrSource: null,
      codeUnavailable: false,
    };
  }
  return {
    state: ticket.isPending ? 'loading' : 'ready',
    eventName: entry.event.name,
    when: formatTicketDateTime(entry.event),
    where: venueLine(entry.event.venueName, publicEvent.data?.location.city ?? null),
    name: registrantName(user?.displayName ?? null),
    confirmationCode: entry.registration.confirmationCode,
    qrSource,
    codeUnavailable: payload === null && !ticket.isFetching,
  };
}

export function TicketCard({
  content,
  className,
}: {
  readonly content: TicketContent;
  readonly className?: string;
}): React.ReactElement {
  return (
    <section
      data-testid="ticket-card"
      className={className ?? 'overflow-hidden rounded-xl bg-background shadow-xl'}
    >
      <div className="bg-brand-800 p-5 text-center">
        <p className="font-brand text-2xs font-bold uppercase tracking-[0.14em] text-fuchsia-200">
          {TICKET_BAND_LABEL}
        </p>
      </div>

      {/* The event, above the code — what this ticket is for, before what it does. The divider is
          the prototype's 2px dashed line, which is what makes the card read as a ticket. */}
      <div className="border-b-2 border-dashed border-border px-[22px] pb-4 pt-[22px] text-center">
        <p className="font-brand text-xl font-black text-foreground">{content.eventName}</p>
        {content.when === null ? null : (
          <p className="mt-0.5 text-sm text-muted-foreground">{content.when}</p>
        )}
        {content.where === null ? null : (
          <p className="mt-0.5 text-xs text-text-subtle">{content.where}</p>
        )}
      </div>

      <div className="flex flex-col items-center p-[22px]">
        {/*
          The code sits on a plain white panel of its own rather than on the card's surface. Quiet
          zone is a scanning requirement — a QR needs light margin on all four sides to be found at
          all — and giving it a dedicated panel means a later change to the card's background
          cannot quietly cost the door a read.
        */}
        <div className="h-[168px] w-[168px] rounded-md border border-border-subtle bg-white p-3">
          {content.qrSource === null ? (
            <Skeleton className="h-full w-full rounded-sm" />
          ) : (
            <img src={content.qrSource} alt={QR_ALTERNATIVE_TEXT} className="h-full w-full" />
          )}
        </div>

        {/*
          `MEM-022` AC-1's closing pair: who this admits, and under which order. The name is what a
          volunteer checks against the person in front of them; the order number is what either of
          them reads aloud when the scan will not take.
        */}
        {content.name === null ? null : (
          <p className="mt-3.5 font-brand text-sm font-bold text-foreground">{content.name}</p>
        )}
        <p className="text-xs text-muted-foreground">{ticketLine(content.confirmationCode)}</p>
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-text-subtle">
          <ScanLine className="h-3.5 w-3.5" aria-hidden />
          {SCAN_AT_CHECK_IN}
        </p>

        {/* The token failed to render into a code. Say so plainly and name the fallback that
            actually works at a door, rather than leaving a blank square above a name. */}
        {content.codeUnavailable ? (
          <div className="mt-3 rounded-lg bg-muted/60 p-3.5 text-center text-sm text-muted-foreground">
            <p className="font-semibold text-foreground">{TICKET_UNAVAILABLE_TITLE}</p>
            <p>{TICKET_UNAVAILABLE_DESCRIPTION}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * The token, drawn.
 *
 * Rendering is asynchronous — the encoder returns a promise — so it cannot happen during render,
 * and the effect is guarded against resolving into an unmounted component or, worse, into a
 * *different* member's ticket after a fast navigation. A failure sets no source, which surfaces
 * as the "not ready" notice rather than as a broken image.
 */
function useQrCodeSource(payload: string | null): string | null {
  const [source, setSource] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (payload === null) {
      setSource(null);
      return;
    }
    let live = true;
    void renderQrSvg(payload, TICKET_QR_OPTIONS)
      .then((svg) => {
        if (live) setSource(qrSvgDataUri(svg));
      })
      .catch(() => {
        if (live) setSource(null);
      });
    return () => {
      live = false;
    };
  }, [payload]);

  return source;
}
